import React, { useEffect, useRef, useState } from "react";
import styles from "./AchievementsContainer.module.css";
import { ACHIEVEMENTS_DATA, ACHIEVEMENTS_SETTINGS, PRESS_DATA } from "../../Constants";

const AchievementsContainer = () => {
  const [counts, setCounts] = useState(ACHIEVEMENTS_DATA.map(() => 0));
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [itemsToShow, setItemsToShow] = useState(3);
  const [transitionEnabled, setTransitionEnabled] = useState(true);

  const sectionRef = useRef(null);
  const animatedRef = useRef(false);

  // --- Continuous Loop Setup ---
  // Clone the first few items and add them to the end
  const extendedPressData = [...PRESS_DATA, ...PRESS_DATA.slice(0, itemsToShow)];
  const totalRealItems = PRESS_DATA.length;

  useEffect(() => {
    const handleResize = () => {
      setItemsToShow(window.innerWidth <= 900 ? 1 : 3);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // --- Number Counter Animation (Kept as is) ---
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !animatedRef.current) {
        animatedRef.current = true;
        animateNumbers();
      }
    }, { threshold: ACHIEVEMENTS_SETTINGS.observerThreshold });
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const animateNumbers = () => {
    const duration = ACHIEVEMENTS_SETTINGS.animationDuration;
    const startTime = performance.now();
    const animate = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      setCounts(ACHIEVEMENTS_DATA.map((item) => Math.floor(item.number * progress)));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  };

  // --- Continuous Auto-Scroll Logic ---
  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setTransitionEnabled(true);
      setCurrentIndex((prev) => prev + 1);
    }, 3000);

    return () => clearInterval(interval);
  }, [isPaused, itemsToShow]);

  // Handle the "Infinite" jump
  useEffect(() => {
    // If we reached the start of the cloned items
    if (currentIndex === totalRealItems) {
      const timer = setTimeout(() => {
        setTransitionEnabled(false); // Disable animation
        setCurrentIndex(0); // Jump to actual start
      }, 800); // This must match the CSS transition duration (0.8s)
      return () => clearTimeout(timer);
    }
  }, [currentIndex, totalRealItems]);

  return (
    <div className={styles.wrapper}>
      {/* Achievements Counters Section */}
      <section ref={sectionRef} className={styles.container}>
        {ACHIEVEMENTS_DATA.map((item, index) => (
          <div key={index} className={`${styles.card} ${index !== ACHIEVEMENTS_DATA.length - 1 ? styles.borderRight : ""}`}>
            <div className={styles.numberWrapper}>
              <span className={styles.number}>{counts[index]}</span>
              <span className={styles.plus}>+</span>
            </div>
            <p className={styles.label}>{item.label}</p>
          </div>
        ))}
      </section>

      {/* Press Slider Section */}
      <section 
        className={styles.pressSection}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <div className={styles.pressViewport}>
          <div 
            className={styles.pressTrack} 
            style={{ 
              transform: `translateX(-${currentIndex * (100 / itemsToShow)}%)`,
              transition: transitionEnabled ? "transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)" : "none"
            }}
          >
            {extendedPressData.map((item, index) => (
              <div key={index} className={styles.pressSlide}>
                <div className={styles.pressItem}>
                  <div className={styles.logoWrapper}>
                    <img src={item.logo} alt={item.source} className={styles.pressLogo} />
                  </div>
                  <p className={styles.quote}>"{item.quote}"</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Dots - Only show dots for the "Real" items */}
        <div className={styles.dots}>
          {PRESS_DATA.map((_, index) => (
            <span
              key={index}
              // We use modulo so the dot highlights correctly even during the jump
              className={(currentIndex % totalRealItems) === index ? styles.active : ""}
              onClick={() => {
                setTransitionEnabled(true);
                setCurrentIndex(index);
              }}
            ></span>
          ))}
        </div>
      </section>
    </div>
  );
};

export default AchievementsContainer;